import { IS_DEVELOPMENT, TRACKING_ENABLED } from './environment';
import {
  AnalyticsProvider,
  AnalyticsConfig,
  EventCategories,
  EventTypes,
} from './analytics-provider';
import { PostHogProvider } from './providers/posthog-provider';
import { GoogleAnalyticsProvider } from './providers/google-analytics-provider';
import {
  ONBOARDING_STEPS,
  OnboardingStepName,
  DEFAULT_TOTAL_ONBOARDING_STEPS,
} from '@constants/onboarding';

interface OnboardingStepData {
  step_number: number;
  step_name: OnboardingStepName;
  total_steps: number;
  step_data: Record<string, unknown>;
  timestamp: string;
  user_progress_percentage: number;
  time_since_start_seconds?: number;
}

/**
 * Enhanced Analytics service supporting multiple providers (PostHog, Google Analytics)
 */
class AnalyticsService {
  private providers: AnalyticsProvider[] = [];
  private onboardingStartTime: string | null = null;
  private config: AnalyticsConfig;

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.initializeProviders();
  }

  private initializeProviders() {
    if (this.config.enablePostHog) {
      this.providers.push(new PostHogProvider());
    }

    if (this.config.enableGoogleAnalytics) {
      this.providers.push(new GoogleAnalyticsProvider());
    }
  }

  /**
   * Execute a method across all enabled providers
   */
  private executeAcrossProviders<T extends keyof AnalyticsProvider>(
    method: T,
    ...args: Parameters<AnalyticsProvider[T]>
  ) {
    this.providers.forEach(provider => {
      try {
        (provider[method] as (...args: unknown[]) => void)(...args);
      } catch (error) {
        console.warn(`Analytics provider error for ${method}:`, error);
      }
    });
  }

  // ===== CORE TRACKING METHODS =====

  /**
   * Track any custom event
   */
  track(eventName: string, properties?: Record<string, unknown>) {
    this.executeAcrossProviders('trackEvent', eventName, properties);
  }

  /**
   * Track page views
   */
  trackPageView(pageName: string, properties?: Record<string, unknown>) {
    this.executeAcrossProviders('trackPageView', pageName, properties);
  }

  /**
   * Identify user
   */
  identifyUser(userId: string, properties?: Record<string, unknown>) {
    this.executeAcrossProviders('identifyUser', userId, properties);
  }

  /**
   * Update user profile
   */
  updateUserProfile(properties: Record<string, unknown>) {
    this.executeAcrossProviders('updateUserProfile', properties);
  }

  /**
   * Track errors
   */
  trackError(error: Error | unknown, context?: Record<string, unknown>) {
    this.executeAcrossProviders('trackError', error, context);
  }

  /**
   * Reset analytics (clear user data)
   */
  reset() {
    this.executeAcrossProviders('reset');
    this.onboardingStartTime = null;
  }

  // ===== ONBOARDING TRACKING =====

  trackOnboardingStart(totalSteps: number = DEFAULT_TOTAL_ONBOARDING_STEPS) {
    this.onboardingStartTime = new Date().toISOString();

    this.track(EventTypes.ONBOARDING_STARTED, {
      event_category: EventCategories.ONBOARDING,
      total_steps: totalSteps,
      started_at: this.onboardingStartTime,
    });
  }

  trackOnboardingStep(
    stepNumber: number,
    stepData: Record<string, unknown>,
    totalSteps: number = DEFAULT_TOTAL_ONBOARDING_STEPS
  ) {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];
    const timestamp = new Date().toISOString();
    const progressPercentage = Math.round((stepNumber / totalSteps) * 100);

    const eventData: OnboardingStepData = {
      step_number: stepNumber,
      step_name: stepName,
      total_steps: totalSteps,
      step_data: this.sanitizeStepData(stepData),
      timestamp,
      user_progress_percentage: progressPercentage,
    };

    // Add timing data if we have the start time
    if (this.onboardingStartTime) {
      const currentTime = new Date(timestamp);
      const startTime = new Date(this.onboardingStartTime);

      if (!isNaN(currentTime.getTime()) && !isNaN(startTime.getTime())) {
        const timeSpent = currentTime.getTime() - startTime.getTime();
        eventData.time_since_start_seconds = Math.round(timeSpent / 1000);
      }
    }

    this.track(EventTypes.ONBOARDING_STEP_COMPLETED, {
      event_category: EventCategories.ONBOARDING,
      ...eventData,
    });

    // Track specific step completion for easier filtering
    this.track(`onboarding_step_${stepName}_completed`, {
      event_category: EventCategories.ONBOARDING,
      step_number: stepNumber,
      ...this.sanitizeStepData(stepData),
    });
  }

  trackOnboardingStepView(
    stepNumber: number,
    totalSteps: number = DEFAULT_TOTAL_ONBOARDING_STEPS
  ) {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];

    this.track(EventTypes.ONBOARDING_STEP_VIEWED, {
      event_category: EventCategories.ONBOARDING,
      step_number: stepNumber,
      step_name: stepName,
      total_steps: totalSteps,
    });
  }

  trackOnboardingBackNavigation(fromStep: number, toStep: number) {
    this.track('onboarding_back_navigation', {
      event_category: EventCategories.ONBOARDING,
      from_step: fromStep,
      to_step: toStep,
      from_step_name:
        ONBOARDING_STEPS[fromStep as keyof typeof ONBOARDING_STEPS],
      to_step_name: ONBOARDING_STEPS[toStep as keyof typeof ONBOARDING_STEPS],
    });
  }

  trackOnboardingAbandonment(currentStep: number, totalSteps: number) {
    const stepName =
      ONBOARDING_STEPS[currentStep as keyof typeof ONBOARDING_STEPS];
    const progressPercentage = Math.round((currentStep / totalSteps) * 100);

    this.track(EventTypes.ONBOARDING_ABANDONED, {
      event_category: EventCategories.ONBOARDING,
      abandoned_at_step: currentStep,
      abandoned_at_step_name: stepName,
      progress_percentage: progressPercentage,
      total_steps: totalSteps,
    });
  }

  trackOnboardingCompletion(
    totalSteps: number,
    completionData: Record<string, unknown>
  ) {
    const timestamp = new Date().toISOString();
    let completionTime: number | undefined;

    if (this.onboardingStartTime) {
      completionTime =
        new Date(timestamp).getTime() -
        new Date(this.onboardingStartTime).getTime();
    }

    this.track(EventTypes.ONBOARDING_COMPLETED, {
      event_category: EventCategories.ONBOARDING,
      total_steps: totalSteps,
      completed_at: timestamp,
      completion_time_seconds: completionTime
        ? Math.round(completionTime / 1000)
        : undefined,
      completion_data: this.sanitizeStepData(completionData),
    });

    // Reset the start time
    this.onboardingStartTime = null;
  }

  // ===== FORM TRACKING =====

  trackFormStart(formName: string, stepName?: OnboardingStepName) {
    this.track(EventTypes.FORM_STARTED, {
      event_category: EventCategories.FORM,
      form_name: formName,
      step_name: stepName,
    });
  }

  trackFormSubmit(
    formName: string,
    stepName?: OnboardingStepName,
    formData?: Record<string, unknown>
  ) {
    this.track(EventTypes.FORM_SUBMITTED, {
      event_category: EventCategories.FORM,
      form_name: formName,
      step_name: stepName,
      form_data: this.sanitizeStepData(formData || {}),
    });
  }

  trackFormFieldInteraction(
    fieldName: string,
    stepName: OnboardingStepName,
    interactionType: 'focus' | 'blur' | 'change',
    value?: unknown
  ) {
    this.track(EventTypes.FIELD_INTERACTION, {
      event_category: EventCategories.FORM,
      field_name: fieldName,
      step_name: stepName,
      interaction_type: interactionType,
      has_value: value !== undefined && value !== null && value !== '',
    });
  }

  trackFormValidationError(
    fieldName: string,
    stepName: OnboardingStepName,
    errorMessage: string
  ) {
    this.track(EventTypes.FORM_ERROR, {
      event_category: EventCategories.FORM,
      field_name: fieldName,
      step_name: stepName,
      error_message: errorMessage,
    });
  }

  // ===== CTA & BUTTON TRACKING =====

  trackCTAClick(
    ctaName: string,
    location: string,
    additionalData?: Record<string, unknown>
  ) {
    this.track(EventTypes.CTA_CLICKED, {
      event_category: EventCategories.CTA,
      cta_name: ctaName,
      cta_location: location,
      ...additionalData,
    });
  }

  trackButtonClick(
    buttonName: string,
    location: string,
    additionalData?: Record<string, unknown>
  ) {
    this.track(EventTypes.BUTTON_CLICKED, {
      event_category: EventCategories.CTA,
      button_name: buttonName,
      button_location: location,
      ...additionalData,
    });
  }

  // ===== CONVERSION TRACKING =====

  trackConversion(
    conversionName: string,
    value?: number,
    additionalData?: Record<string, unknown>
  ) {
    this.track(EventTypes.CONVERSION, {
      event_category: EventCategories.CONVERSION,
      conversion_name: conversionName,
      value,
      ...additionalData,
    });
  }

  trackGoalCompletion(
    goalName: string,
    value?: number,
    additionalData?: Record<string, unknown>
  ) {
    this.track(EventTypes.GOAL_COMPLETED, {
      event_category: EventCategories.CONVERSION,
      goal_name: goalName,
      value,
      ...additionalData,
    });
  }

  // ===== SPECIALIZED TRACKING =====

  trackInvestmentSelection(
    investmentId: string,
    investmentData: Record<string, unknown>
  ) {
    this.track('investment_selected', {
      event_category: 'investment',
      investment_id: investmentId,
      ...this.sanitizeStepData(investmentData),
    });
  }

  trackInvestmentDeselection(investmentId: string) {
    this.track('investment_deselected', {
      event_category: 'investment',
      investment_id: investmentId,
    });
  }

  trackTaxSavingsCalculated(amount: number, stepData: Record<string, unknown>) {
    this.track('tax_savings_calculated', {
      event_category: 'calculation',
      estimated_savings: amount,
      calculation_inputs: this.sanitizeStepData(stepData),
    });
  }

  // ===== PRIVATE HELPERS =====

  private sanitizeStepData(
    data: Record<string, unknown>
  ): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      // Skip sensitive data or functions
      if (typeof value === 'function') continue;
      if (key.toLowerCase().includes('password')) continue;
      if (key.toLowerCase().includes('ssn')) continue;
      if (key.toLowerCase().includes('social')) continue;

      // Convert complex objects to simplified representations
      if (value && typeof value === 'object' && value.constructor === Object) {
        sanitized[key] = this.sanitizeStepData(
          value as Record<string, unknown>
        );
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(item =>
          typeof item === 'object' && item
            ? this.sanitizeStepData(item as Record<string, unknown>)
            : item
        );
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }
}

// Create and export singleton instance
const analyticsConfig: AnalyticsConfig = {
  enablePostHog: TRACKING_ENABLED, // Enable PostHog only when tracking is enabled
  enableGoogleAnalytics: TRACKING_ENABLED, // Enable Google Analytics only when tracking is enabled
  isDevelopment: IS_DEVELOPMENT,
};

export const analytics = new AnalyticsService(analyticsConfig);
